import { eloSelectionOpacity } from "./C1_eloSelectionFocus";
import { useEffect, useRef, useState } from "react";
import { useDelayedClick } from "../../../src/utils/useDelayedClick";
import { resolvePlanDisplayMode } from "./C1_editModePresentation";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_Room } from "../model/C1_Room";
import c1_state from "../model/C1_State";
import type { ResizePreview } from "../types/C1_AppTypes";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { clientToPlan } from "./C1_planCoordinates";
import { openUnplacedRoomChapter } from "../model/C1_openUnplacedRoomChapter";

export function C1_RoomTextSvgView({
  floor,
  resizePreview,
  onRoomDoubleClick,
}: {
  floor: C1_Floor;
  resizePreview?: ResizePreview | null;
  onRoomDoubleClick?: (room: C1_Room, clientPoint: { x: number; y: number }) => void;
}) {
  useOnChangeWatcher(c1_state.project.displayMode);
  const view = c1_state.view;
  const PX = c1_state.svgPx;
  const selection = c1_state.selection;
  const renderOptions = c1_state.floorImageRenderOptions.value;
  const showExportNames = renderOptions?.includeRoomNames === true;
  const showEditorNames = !renderOptions;
  const [editing, setEditing] = useState<{ room: C1_Room; name: string } | null>(null);
  const draggedRoom = useRef<C1_Room | null>(null);
  const delayedClick = useDelayedClick(`${floor?.id}:${c1_state.editMode.value}:${selection?.id}:${!!renderOptions}`, 350);
  useEffect(() => {
    setEditing((current) => current &&
      (current.room !== selection || c1_state.editMode.value !== "building" || renderOptions || !floor?.rooms.includes(current.room))
      ? null : current);
  }, [floor, selection, c1_state.editMode.value, renderOptions]);

  if (!floor) return null;
  if (!showExportNames && !showEditorNames) return null;

  return (
    <g className="C1_roomTextSvg" data-c1-svg-layer="room-text" pointerEvents="none">
      {floor.rooms.map((room) => {
        const selected = selection === room;
        if (showEditorNames && selected && c1_state.editMode.value === "building" && (room.owner?.name ?? "") === "") return null;
        const showMasterIndex =
          showEditorNames &&
          resolvePlanDisplayMode(state.settings.applicationMode, c1_state.editMode.value, c1_state.project.displayMode.value, c1_state.floorImageRenderOptions.value?.displayMode) !== "design" &&
          (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") &&
          room.masterIndex !== undefined;
        const masterIndexLabel = String(room.masterIndex ?? "");
        const masterIndexWidth = Math.max(22, masterIndexLabel.length * 6 + 10);
        const fontSize = 15 * (PX / 70);
        const estimatedRoomNameWidth = (room.owner?.name ?? "").length * fontSize * 0.55;
        const labelGap = 6;
        const combinedWidth = masterIndexWidth + labelGap + estimatedRoomNameWidth;

        const rect =
          resizePreview?.room === room && !resizePreview.extrudesWall
            ? resizePreview.rect
            : room.rect(c1_state.project);
        const textPosition = room.roomTextPosition;
        const centerX = (rect.x1 + (rect.x2 - rect.x1) * textPosition.x - view.x1) * PX;
        const roomTextY = (rect.y1 + (rect.y2 - rect.y1) * textPosition.y - view.y1) * PX;
        let dragStart:
          | {
              clientX: number;
              clientY: number;
              planX: number;
              planY: number;
              x: number;
              y: number;
              nextX: number;
              nextY: number;
              moved: boolean;
            }
          | undefined;
        const editMode = c1_state.editMode.value;
        const interactive = showEditorNames && (editMode === "building" || editMode === "furnishing" || (editMode === "EloConcept" || editMode === "EloDetail"));
        const pointerHandlers = interactive
          ? {
              pointerEvents: "all" as const,
              className: "C1_roomTextDragTarget",
              style: { touchAction: "none" },
              onClick: (event: React.MouseEvent<SVGElement>) => {
                event.stopPropagation();
                if (draggedRoom.current === room) return;
                delayedClick.schedule(() => {
                  if (openUnplacedRoomChapter(room)) return;
                  c1_state.setSelection(c1_state.selection === room && ["EloConcept", "EloDetail", "interactionConcept"].includes(editMode) ? undefined : room);
                  if (editMode === "building") setEditing({ room, name: (room.owner?.name ?? "") });
                }, event.detail);
              },
              onDoubleClick: (event: React.MouseEvent<SVGElement>) => {
                delayedClick.cancel();
                event.preventDefault();
                event.stopPropagation();
                onRoomDoubleClick?.(room, { x: event.clientX, y: event.clientY });
              },
              onPointerDown: (event: React.PointerEvent<SVGElement>) => {
                if (event.button !== 0) return;
                delayedClick.cancel();
                draggedRoom.current = null;
                const pointer = clientToPlan(event.currentTarget, event.clientX, event.clientY, view);
                if (!pointer) return;
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.setPointerCapture(event.pointerId);
                dragStart = {
                  clientX: event.clientX,
                  clientY: event.clientY,
                  planX: pointer.x,
                  planY: pointer.y,
                  x: textPosition.x,
                  y: textPosition.y,
                  nextX: textPosition.x,
                  nextY: textPosition.y,
                  moved: false,
                };
              },
              onPointerMove: (event: React.PointerEvent<SVGElement>) => {
                if (!dragStart || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
                const dx = event.clientX - dragStart.clientX;
                const dy = event.clientY - dragStart.clientY;
                if (!dragStart.moved && Math.hypot(dx, dy) < 3) return;
                const pointer = clientToPlan(event.currentTarget, event.clientX, event.clientY, view);
                if (!pointer) return;
                dragStart.moved = true;
                const planDx = pointer.x - dragStart.planX;
                const planDy = pointer.y - dragStart.planY;
                dragStart.nextX = dragStart.x + planDx / Math.max(rect.x2 - rect.x1, 0.001);
                dragStart.nextY = dragStart.y + planDy / Math.max(rect.y2 - rect.y1, 0.001);
                event.currentTarget.setAttribute("transform", `translate(${planDx * PX} ${planDy * PX})`);
              },
              onPointerUp: (event: React.PointerEvent<SVGElement>) => {
                event.stopPropagation();
                const completedDrag = dragStart;
                const moved = completedDrag?.moved === true;
                dragStart = undefined;
                event.currentTarget.removeAttribute("transform");
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                  event.currentTarget.releasePointerCapture(event.pointerId);
                }
                if (moved && completedDrag) {
                  draggedRoom.current = room;
                  room.setRoomTextPosition({ x: completedDrag.nextX, y: completedDrag.nextY });
                  state.undoManager.userInteractionEnded("Raumtext verschieben");
                }
              },
            }
          : {};

        if (showEditorNames && editMode === "building" && selected && editing?.room === room) {
          const inputWidth = Math.max(120, estimatedRoomNameWidth + 24);
          const finishEditing = () => {
            if (editing.name !== (room.owner?.name ?? "")) {
              (room.owner && (room.owner.name = editing.name));
              state.undoManager.userInteractionEnded("Raumname ändern");
            }
            setEditing(null);
          };
          return (
            <foreignObject key={room.id} x={centerX - inputWidth / 2} y={roomTextY - fontSize} width={inputWidth} height={fontSize * 2 + 4} pointerEvents="all"
              onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
              <input
                aria-label="Raumname"
                data-testid={`room-name-input-${room.id}`}
                value={editing.name}
                ref={(input) => { if (input && document.activeElement !== input) { input.focus(); input.select(); } }}
                style={{ width: "100%", height: fontSize * 2, boxSizing: "border-box", fontFamily: "Inter, system-ui, sans-serif", fontSize, fontWeight: 700, textAlign: "center", color: "#213547" }}
                onChange={(event) => setEditing({ room, name: event.target.value })}
                onBlur={finishEditing}
                onDoubleClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Enter") finishEditing();
                  else if (event.key === "Escape") setEditing(null);
                }}
              />
            </foreignObject>
          );
        }

        if (!showMasterIndex) {
          return (
            <g key={room.id} data-c1-room-id={room.id} {...pointerHandlers} opacity={eloSelectionOpacity(room, c1_state)}>
              <text
                className="C1_roomTextLabel"
                x={centerX}
                y={roomTextY}
                fill="#213547"
                fontFamily="Inter, system-ui, sans-serif"
                fontSize={fontSize}
                fontWeight={700}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {(room.owner?.name ?? "")}
              </text>
            </g>
          );
        }

        const contentStartX = centerX - combinedWidth / 2;
        return (
          <g key={room.id} data-c1-room-id={room.id} {...pointerHandlers} opacity={eloSelectionOpacity(room, c1_state)}>
            <g className="C1_roomTextLabel">
              <rect x={contentStartX} y={roomTextY - 9} width={masterIndexWidth} height={18} rx={9} fill="#173f56" />
              <text
                x={contentStartX + masterIndexWidth / 2}
                y={roomTextY}
                fill="#fff"
                fontSize={10}
                fontWeight={400}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {masterIndexLabel}
              </text>
              <text
                x={contentStartX + masterIndexWidth + labelGap}
                y={roomTextY}
                fill="#213547"
                fontFamily="Inter, system-ui, sans-serif"
                fontSize={fontSize}
                fontWeight={700}
                textAnchor="start"
                dominantBaseline="central"
              >
                {(room.owner?.name ?? "")}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
}
